import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mockProducts } from '../../../mocks/products';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const results = query.length > 1
    ? mockProducts.filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.category_name.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 6)
    : [];

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      document.body.style.overflow = 'hidden';
    } else {
      setQuery('');
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/urunler?q=${encodeURIComponent(query.trim())}`);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center pt-24 px-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Search Input */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-5 py-4 border-b border-cream-dark">
          <i className="ri-search-line text-coffee-light text-xl flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Kahve adı veya kategori ara..."
            className="flex-1 text-base text-coffee placeholder-coffee-light/50 bg-transparent outline-none"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} className="text-coffee-light hover:text-coffee transition-colors">
              <i className="ri-close-line text-xl" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-coffee-light/70 hover:text-coffee transition-colors ml-2 whitespace-nowrap"
          >
            ESC
          </button>
        </form>

        {/* Results */}
        {results.length > 0 && (
          <div className="max-h-80 overflow-y-auto divide-y divide-cream-dark">
            {results.map(product => (
              <Link
                key={product.id}
                to={`/urun/${product.slug}`}
                onClick={onClose}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-cream transition-colors"
              >
                <div className="w-12 h-12 bg-cream rounded-xl overflow-hidden flex-shrink-0">
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-coffee truncate">{product.name}</p>
                  <p className="text-xs text-coffee-light">{product.category_name}</p>
                </div>
                <span className="text-sm font-bold text-coffee flex-shrink-0">{product.price}₺</span>
              </Link>
            ))}
          </div>
        )}

        {/* Empty State */}
        {query.length > 1 && results.length === 0 && (
          <div className="px-5 py-8 text-center">
            <i className="ri-search-line text-3xl text-coffee-light/30 mb-3 block" />
            <p className="text-sm text-coffee-light">&ldquo;{query}&rdquo; için sonuç bulunamadı</p>
          </div>
        )}

        {/* Quick Links */}
        {query.length <= 1 && (
          <div className="px-5 py-4">
            <p className="text-xs text-coffee-light font-medium uppercase tracking-wider mb-3">Popüler Aramalar</p>
            <div className="flex flex-wrap gap-2">
              {['Etiyopya', 'Cold Brew', 'Espresso', 'Hediye Seti', 'Filtre'].map(tag => (
                <button
                  key={tag}
                  onClick={() => setQuery(tag)}
                  className="px-3 py-1.5 bg-cream text-coffee-medium text-xs font-medium rounded-lg hover:bg-gold/10 hover:text-gold transition-colors"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
